// src/slices/cartSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAxiosConfig } from '../utils/http';

// ASYNC: Add to Cart
export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async ({ id, qty, selectedColorsVariants, variantSignature }, { getState, rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/products/${id}`);
      if (data && data.disabled) {
        return rejectWithValue('This product is currently unavailable.');
      }

      // If product uses new variant system but variantSignature not provided, prevent adding a blank duplicate line item
      if (Array.isArray(data.variantProps) && data.variantProps.length > 0 && Array.isArray(data.skus) && data.skus.length > 0) {
        if (!variantSignature) {
          return rejectWithValue('Please select all required variant options before adding to cart.');
        }
      }

      // derive variant price
      let price = data.price;
      let image = data.image;
      let countInStock = data.countInStock;
      let variantValues = [];
      let resolvedSignature = variantSignature || '';
      let skuUnavailable = false;

      if (Array.isArray(data.colorsVariants) && selectedColorsVariants) {
        const v = data.colorsVariants.find((cv) => cv?.name === selectedColorsVariants);
        if (v && v.unavailable) {
          return rejectWithValue('This variant isn’t available and cannot be added to your cart.');
        }
        if (v && typeof v.price === 'number' && !Number.isNaN(v.price) && v.price >= 0) {
          price = v.price;
        }
        if (v && v.src) {
          image = v.src;
        }
      }

      // New variant system handling via variantSignature (propId=valueId;...)
      if (resolvedSignature && Array.isArray(data.variantProps) && Array.isArray(data.skus)) {
        // Parse signature into map
        const sigPairs = resolvedSignature.split(';').filter(Boolean).map(s => s.split('='));
        const sigMap = new Map(sigPairs.map(([k,v]) => [k,v]));
        // Build variantValues array with names
        for (const prop of data.variantProps) {
          const vid = sigMap.get(prop.id);
          if (!vid) continue;
            const valObj = (prop.values || []).find(v => v.id === vid);
            variantValues.push({
              propId: prop.id,
              propName: prop.name,
              valueId: vid,
              valueName: valObj ? valObj.name : vid,
            });
        }
        // Find matching SKU
        const matchingSku = data.skus.find(sku => {
          const key = sku.key || {};
          return data.variantProps.every(p => {
            const expected = sigMap.get(p.id);
            return expected && (key[p.id] === expected || key[p.id] === (p.values||[]).find(v => v.id === expected)?.name);
          });
        });
        if (matchingSku) {
          if (matchingSku.unavailable) {
            skuUnavailable = true;
          }
          if (matchingSku.price !== undefined && matchingSku.price !== null && !Number.isNaN(+matchingSku.price)) {
            price = +matchingSku.price;
          }
          if (matchingSku.image) {
            image = matchingSku.image;
          } else {
            // Fallback: use first image-type variant value image
            for (const prop of data.variantProps) {
              if (prop.type === 'image') {
                const vid = sigMap.get(prop.id);
                const valObj = (prop.values||[]).find(v => v.id === vid);
                if (valObj?.image) { image = valObj.image; break; }
              }
            }
          }
          if (matchingSku.countInStock !== undefined) {
            countInStock = matchingSku.countInStock;
          }
        } else {
          // Combination not found among SKUs
          return rejectWithValue('This variant combination is unavailable.');
        }
      }

      if (skuUnavailable) {
        return rejectWithValue('This variant isn’t available and cannot be added to your cart.');
      }

      const item = {
        product: data._id,
        name: data.name,
        image,
        price,
        countInStock,
        selectedColorsVariants,
        variantSignature: resolvedSignature || null,
        variantValues,
        qty: Number(qty) || 1,
      };

      return item;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ASYNC: Fetch persisted cart from server (for authenticated users)
export const fetchServerCart = createAsyncThunk(
  'cart/fetchServerCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state);
      const { data } = await axios.get('/api/cart', config);
      return data.items || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ASYNC: Save cart to server (for authenticated users)
export const saveServerCart = createAsyncThunk(
  'cart/saveServerCart',
  async (items, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state);
      await axios.put('/api/cart', { items }, config);
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Initialize from localStorage (resilient to JSON errors)
const loadLocal = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch { return fallback; }
};

const initialCartItems = loadLocal('cartItems', []);
const initialShipping = loadLocal('shippingAddress', { email: '' });
const initialSelectedRate = loadLocal('selectedRate', {});
const initialPaymentMethod = loadLocal('paymentMethod', '');

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    cartItems: initialCartItems,
    shippingAddress: initialShipping,
    shipToFirstName: initialShipping.shipToFirstName || '',
    shipToLastName: initialShipping.shipToLastName || '',
    selectedRate: initialSelectedRate,
    paymentMethod: initialPaymentMethod,
    error: null,
  },
  reducers: {
    removeFromCart: (state, action) => {
      const { productId, selectedColorsVariants, variantSignature } = action.payload || {};
      state.cartItems = state.cartItems.filter((x) => {
        if (x.product !== productId) return true;
        if (variantSignature) {
          // Remove only the matching signature
          return x.variantSignature !== variantSignature;
        }
        // Legacy removal: only remove items that also have no signature
        return !(x.variantSignature == null && x.selectedColorsVariants === selectedColorsVariants);
      });
      localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
    },
    updateCartItemQty: (state, action) => {
      const { productId, selectedColorsVariants, variantSignature, qty } = action.payload || {};
      const item = state.cartItems.find((x) => {
        if (x.product !== productId) return false;
        if (variantSignature) return x.variantSignature === variantSignature;
        return x.variantSignature == null && x.selectedColorsVariants === selectedColorsVariants;
      });
      if (item) {
        item.qty = Number(qty) || 1;
        localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
      }
    },
    saveShippingAddress: (state, action) => {
      state.shippingAddress = { email: '', ...(action.payload || {}) };
      // Backward compatibility duplication
      if (action.payload) {
        state.shipToFirstName = action.payload.shipToFirstName || '';
        state.shipToLastName = action.payload.shipToLastName || '';
      }
      localStorage.setItem('shippingAddress', JSON.stringify(state.shippingAddress));
    },
    saveSelectedRate: (state, action) => {
      state.selectedRate = action.payload;
      localStorage.setItem('selectedRate', JSON.stringify(state.selectedRate));
    },
    savePaymentMethod: (state, action) => {
      state.paymentMethod = action.payload;
      localStorage.setItem('paymentMethod', JSON.stringify(state.paymentMethod));
    },
    clearCart: (state) => {
      state.cartItems = [];
      localStorage.removeItem('cartItems');
    },
    clearAllCheckout: (state) => {
      state.cartItems = [];
      state.shippingAddress = {
        shipToFirstName: 'guestFirstName',
        shipToLastName: 'guestLastName',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        email: '',
      };
      state.shipToFirstName = 'guestFirstName';
      state.shipToLastName = 'guestLastName';
      state.selectedRate = {};
      state.paymentMethod = '';
      localStorage.removeItem('cartItems');
      localStorage.removeItem('shippingAddress');
      localStorage.removeItem('selectedRate');
      localStorage.removeItem('paymentMethod');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addToCart.fulfilled, (state, action) => {
        const item = action.payload;

        const idx = state.cartItems.findIndex((x) => {
          if (item.variantSignature) {
            return x.product === item.product && x.variantSignature === item.variantSignature;
          }
          // Legacy (no signature) only matches other legacy entries (avoid merging with variant entries)
          return x.product === item.product && x.variantSignature == null && x.selectedColorsVariants === item.selectedColorsVariants;
        });

        if (idx > -1) {
          // Merge by increasing qty when the same product+variant exists
          state.cartItems[idx].qty += item.qty;
          // Also update price/countInStock/name/image in case they changed
          state.cartItems[idx].price = item.price;
          state.cartItems[idx].name = item.name;
          state.cartItems[idx].image = item.image;
          state.cartItems[idx].countInStock = item.countInStock;
        } else {
          state.cartItems.push(item);
        }

        // Sanitation: if we just added (or merged) a variantSignature item, drop any legacy blank entry for same product
        if (item.variantSignature) {
          state.cartItems = state.cartItems.filter((x) => {
            if (x.product !== item.product) return true;
            // Keep all signed items and legacy entries that actually have a non-empty selectedColorsVariants
            if (x.variantSignature) return true;
            return !!x.selectedColorsVariants; // remove completely blank legacy duplicate
          });
        }

        localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Merge server cart into local cart on fetch
      .addCase(fetchServerCart.fulfilled, (state, action) => {
        const serverItems = Array.isArray(action.payload) ? action.payload : [];
        const key = (it) => `${it.product}::${it.variantSignature || it.selectedColorsVariants || ''}`;

        // 1) Start with server as source of truth
        const map = new Map(serverItems.map((it) => [key(it), { ...it }]));

        // 2) Add only local items that don't exist on server. Server wins
        // on conflict — this must be idempotent, since it fires on every
        // app mount (reload, new tab, token refresh), not just at login;
        // summing here previously caused quantities to double on every
        // re-fetch once local and server already agreed.
        for (const local of state.cartItems) {
          const k = key(local);
          if (!map.has(k)) {
            map.set(k, { ...local });
          }
        }

        state.cartItems = Array.from(map.values());
        // Additional sanitation after merge: remove any legacy blank entries when signed variants exist
        state.cartItems = state.cartItems.filter((x, _, arr) => {
          if (x.variantSignature == null) {
            const hasSigned = arr.some(y => y.product === x.product && y.variantSignature);
            if (hasSigned && !x.selectedColorsVariants) return false;
          }
          return true;
        });
        try {
          localStorage.setItem('cartItems', JSON.stringify(state.cartItems));
        } catch {}
      });
  },
});

export const {
  removeFromCart,
  updateCartItemQty,
  saveShippingAddress,
  saveSelectedRate,
  savePaymentMethod,
  clearCart,
  clearAllCheckout,
} = cartSlice.actions;

export default cartSlice.reducer;
